import { IkeGeodesic } from '@ikegps/core-measurement';
import type { CaptureFileJSONObject, CollectionFieldAnchorManualFieldValue, TagJSONObject } from '@ikegps/core-api';
import {
	type CollectionModel,
	CollectionModelFieldAnchor,
	CollectionModelFieldLine,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	FormModelFieldSubForm,
} from '@ikegps/core-shared';
import { AnchorInputState } from '../../components/input/anchor.input.state';
import { action, computed, type IReactionDisposer, observable, reaction, when, makeObservable } from 'mobx';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { T, Translate } from '../../i18n';
import { Streetview } from '../../modules/streetview';
import { BoundingString } from '../../modules/tag.map/bounding.string';
import { TopologicalEditorMap } from '../../modules/topological.editor.map';
import { type LegendItem, lineStyleLegendItems } from '../../modules/topological.editor.map/map.legend';
import {
	type PointCollectionLineInfo,
	TopologicalPointCollectionCache,
} from '../../modules/topological.editor.map/point.collection.cache';
import {
	CreatePoleToolController,
	RelocatePoleToolController,
	type TopologicalEditorToolController,
} from '../../modules/topological.editor.tool';
import { Services } from '../../services';
import type { InputEventBindContext } from '../../services/input/event.context';
import { MobxState } from '../../state/mobx';
import { MeasurementState } from '../../state/measurement.state';
import { DistanceMeasureState } from '../../state/distance.measure.state';
import { LineDrawState } from '../../state/line.draw.state';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { TopologicalEditorCollectionEditCtrl } from './topological.editor.collection.edit.ctrl';

export const TOPO_TOOL_LOOKUP = {
	relocatePole: RelocatePoleToolController,
	createPole: CreatePoleToolController,
} as const;

export class TopologicalEditorController extends MobxState {
	private disposers: IReactionDisposer[] = [];
	private boundExitTool: (() => void) | undefined;
	public mapIsReady: boolean = false;

	@observable public newPoleModel: CollectionModel[] = [];

	eventContext: InputEventBindContext | undefined;

	@observable public classnames: string[] = [];

	public measurementState = new MeasurementState();
	public distanceMeasureState = new DistanceMeasureState();

	public map = new TopologicalEditorMap({
		onTagClick: (revision) => {
			// While positioning the print frame, the map is for framing only — ignore pole selection.
			if (this.printFraming) {
				return;
			}
			// Drawing a line locks pole selection (mirrors the measurement/distance tools).
			if (this.lineDrawState.isActive) {
				return;
			}
			// Exit create pole mode when clicking on an existing pole
			if (this.isCreatePoleMode) {
				this.exitTool();
			}
			// Handle measurement mode - set pending measurement collection before selecting
			if (this.measurementState.isActive && !this.measurementState.isPaused) {
				const collection = this.collectionModels.find((c) => c.revision === revision);
				if (collection) {
					this.setPendingMeasurementCollection(collection);
				}
			}
			// Handle distance measure mode - set pending measurement collection before selecting
			if (this.distanceMeasureState.isActive) {
				const collection = this.collectionModels.find((c) => c.revision === revision);
				if (collection) {
					this.setPendingMeasurementCollection(collection);
				}
			}
			this.setSelectedCollectionRevision(revision);
			this.setSelectedVectorId(null);
		},
		onVectorClick: (collectionRevision, vectorId) => {
			if (this.printFraming) {
				return;
			}
			if (this.lineDrawState.isActive) {
				return;
			}
			// Disable vector selection during active measurement
			if (this.measurementState.isActive && !this.measurementState.isPaused) {
				return;
			}
			if (this.distanceMeasureState.isActive) {
				return;
			}
			this.setSelectedCollectionRevision(collectionRevision);
			this.setSelectedVectorId(vectorId);
		},
		onVectorDragStart: () => this.exitTool(),
		onVectorDragEnd: (field, nextVector) => {
			if (CollectionModelFieldAnchor.isAnchorField(field)) {
				// Map drag on an anchor is the equivalent of a manual bearing edit
				const newValue: CollectionFieldAnchorManualFieldValue = {
					...nextVector,
					collectionTool: 'VECTOR',
					placement: 'Other',
					relatedSpanIds: [],
				};
				field.Mutator.manual(newValue);

				const editCtrl = this.getEditCtrlByRevision(field.collection.revision);
				const inputState = editCtrl?.changeState?.getState(field.id);

				if (inputState instanceof AnchorInputState) {
					inputState.setValue(newValue);
				}
			} else {
				field.Mutator.manual(nextVector);
			}
		},
		onMapClick: (position) => {
			if (this.printFraming) {
				return;
			}
			this.handleMapClick(position);
		},
		measurementState: this.measurementState,
		distanceMeasureState: this.distanceMeasureState,
		lineDrawState: this.lineDrawState,
	});
	public streetview = new Streetview();

	@observable public selectedCollectionRevision: string | null = null;
	@observable public selectedCaptureId: string | null = null;

	@observable public selectedVectorId: string | null = null;

	@observable private activeTool: TopologicalEditorToolController | null = null;

	@observable public collectionEditControllers: TopologicalEditorCollectionEditCtrl[] = [];

	@observable public isSaving: boolean = false;

	@observable public isCreatePoleMode: boolean = false;

	// True while the print frame is being positioned; locks map tools and pole/vector selection.
	@observable public printFraming: boolean = false;

	// Collection awaiting measurement point addition (measurement mode only)
	private pendingMeasurementCollection: { collection: CollectionModel } | null = null;

	@computed
	public get isRelocatePoleActive(): boolean {
		return this.activeTool instanceof RelocatePoleToolController;
	}
	@observable public formHasLocationField: boolean = false;

	constructor() {
		super();

		makeObservable(this);

		this.mapIsReady = false;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		Services.Map.isReadyPromise.then(() => {
			Services.Map.map.setOptions({
				fullscreenControl: false,
			});
			Services.Map.map.setStreetView(Services.Streetview.instance);
			Services.Map.map.getStreetView().setVisible(true);
			this.boundExitTool = this.exitTool.bind(this);
			this.bindReactions();
			this.bindOnCollectionModelsUpdate();
			this.bindOnSelectedCollectionUpdate();
			this.centerMapOnLoad();
			this.bindMapMoveRouteUpdate();
			this.mapIsReady = true;
			this.refreshOverlay();
			this.setSelectedCollectionRevision(null);
			this.formHasLocationField = this.getFormHasLocationField();
		});
	}

	public getMapModeMap(): google.maps.Map | null {
		return this.map?.map || null;
	}

	@action.bound
	private handleMapClick(position: google.maps.LatLng) {
		if (this.lineDrawState.isActive) {
			this.lineDrawState.addPoint(this.buildMeasurementPoint(position));
			return;
		}

		// Handle measurement mode clicks (ruling span)
		if (this.measurementState.isActive && !this.measurementState.isPaused) {
			this.measurementState.addPoint(this.buildMeasurementPoint(position));
			return;
		}

		// Handle distance measure mode clicks
		if (this.distanceMeasureState.isActive) {
			this.distanceMeasureState.addPoint(this.buildMeasurementPoint(position));
		}
	}

	private buildMeasurementPoint(position: google.maps.LatLng) {
		let location: google.maps.LatLng;
		let type: 'pole' | 'arbitrary';
		let collectionRevision: string | undefined;

		if (this.pendingMeasurementCollection) {
			const collection = this.pendingMeasurementCollection.collection;
			location = new google.maps.LatLng(collection.location?.latitude ?? 0, collection.location?.longitude ?? 0);
			type = 'pole';
			collectionRevision = collection.revision;
			this.pendingMeasurementCollection = null;
		} else {
			location = position;
			type = 'arbitrary';
			collectionRevision = undefined;
		}

		return {
			location,
			type,
			collectionRevision,
			id: crypto.randomUUID(),
		};
	}

	@action.bound
	public setPendingMeasurementCollection(collection: CollectionModel) {
		this.pendingMeasurementCollection = { collection };
	}

	@computed
	public get tag(): TagJSONObject {
		if (Services.Department.current) {
			return AppStore.Tag.get(Services.Route.params.get('jobId'));
		}

		return null;
	}

	@computed
	public get routeCollectionUUID() {
		return Services.Route.params.get('collectionUUID');
	}

	public get locationFromRoute() {
		const str = Services.Route.params.get('location');
		if (str) {
			return BoundingString.fromString(str);
		}
		return null;
	}

	@computed
	public get tagSummary() {
		if (this.tag) {
			return AppStore.TagSummary.get(this.tag.objectId);
		}

		return null;
	}

	@computed
	public get collectionModels(): CollectionModel[] {
		const collections: CollectionModel[] = [];

		// Add saved collections from tag summary
		if (this.tagSummary) {
			collections.push(
				...this.tagSummary.revisions
					.map((revision) => (AppStore.Collection.has(revision) ? AppStore.Collection.get(revision) : null))
					.filter((collection): collection is CollectionModel => collection != null)
			);
		}

		// Add newly created poles that haven't been saved yet
		collections.push(...this.newPoleModel);
		return collections;
	}

	/**
	 * Gets the form of the most recently updated collection and checks if it has a location field
	 * @returns true if the most recent collection's form has a location field, false otherwise
	 */
	public getFormHasLocationField(): boolean {
		const collections = this.collectionModels;

		if (!collections || collections.length === 0) {
			return false;
		}

		// Sort by updatedAt to find most recent, fallback to createdAt
		const sortedCollections = [...collections].sort((a, b) => {
			const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
			const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
			return bTime - aTime; // Most recent first
		});

		const mostRecentCollection = sortedCollections[0];
		if (!mostRecentCollection) {
			return false;
		}

		return mostRecentCollection.editableLocationField != null;
	}

	@computed
	public get mapViewCollections(): CollectionModel[] {
		let collections: CollectionModel[] = [];

		if (this.tagSummary) {
			collections.push(
				...this.tagSummary.revisions
					.map((revision) =>
						this.getEditCtrlByRevision(revision)
							? this.getEditCtrlByRevision(revision).collectionEditState.collection
							: AppStore.Collection.get(revision)
					)
					.filter((collection): collection is CollectionModel => collection != null)
			);
		}

		// Add newly created poles that haven't been saved yet
		collections.push(...this.newPoleModel);

		return collections;
	}

	@computed
	public get selectedCollection(): CollectionModel | null {
		let result: CollectionModel | null = null;

		if (this.selectedCollectionRevision) {
			const hasEdits = this.collectionEditControllers.some((ctrl) => ctrl.revision === this.selectedCollectionRevision);

			if (hasEdits) {
				result =
					this.collectionEditControllers.find((ctrl) => ctrl.revision === this.selectedCollectionRevision)
						?.collection || null;
			} else {
				result = AppStore.Collection.get(this.selectedCollectionRevision);
			}
		}

		return result;
	}

	@computed
	public get selectedCapture(): CaptureFileJSONObject | null {
		if (this.selectedCollectionRevision) {
			const selectedCollection = AppStore.Collection.get(this.selectedCollectionRevision);
			return selectedCollection.state.getCapture(this.selectedCaptureId);
		}

		return null;
	}

	public getCaptureIds(): string[] {
		const selectedCollectionRevision = this.selectedCollectionRevision;
		let captureIds: string[] = [];
		if (selectedCollectionRevision) {
			const collection = AppStore.Collection.get(selectedCollectionRevision);
			collection?.forEachField((field) => {
				if (CollectionModelFieldTrueSize.isTrueSizeField(field)) {
					const fieldCaptureIds = field.getCaptures().map((capture) => capture.objectId);
					captureIds = captureIds.concat(fieldCaptureIds);
				}
			});
		}
		return captureIds;
	}

	@computed
	public get selectedCollectionEdit(): TopologicalEditorCollectionEditCtrl | null {
		if (this.selectedCollection) {
			return this.getEditCtrlByRevision(this.selectedCollection.revision);
		}

		return undefined;
	}

	@computed
	public get hasChanges(): boolean {
		return this.collectionEditControllers.some((ctrl) => ctrl.collectionEditState.isDirty && ctrl.isValid);
	}

	@action
	public setSelectedCollectionRevision(revision: string | null, captureId?: string) {
		this.selectedCollectionRevision = revision;
		let collection = this.collectionModels.find((cm) => cm.revision === revision);
		if (captureId) {
			this.setSelectedCaptureId(captureId);
		}

		if (this.map && this.map.overlay) {
			this.map.overlay.setSelectedCollectionRevision(revision);
		}

		if (collection) {
			Services.Route.setParam('collectionUUID', collection.uuid, false);
		}
	}

	@action
	public setSelectedCaptureId(captureId?: string) {
		this.selectedCaptureId = captureId;
	}

	@action
	public setSelectedVectorId(id: string | null) {
		this.selectedVectorId = id;
	}

	@action
	createEditControllerForCollection(collection: CollectionModel) {
		if (this.getEditCtrlByRevision(collection.revision)) {
			this.removeEditController(collection.revision);
		}
		this.collectionEditControllers.push(new TopologicalEditorCollectionEditCtrl(collection, this));
	}
	/**
	 * Toggle the create pole mode on/off
	 */
	@action
	public toggleCreatePoleMode() {
		if (this.isCreatePoleMode) {
			this.exitTool();
			this.isCreatePoleMode = false;
		} else {
			this.useTool('createPole');
			this.isCreatePoleMode = true;
		}
	}

	public zoomToSelectedCollection() {
		if (!this.selectedCollection || !this.selectedCollection.location) {
			return;
		}
		const { latitude, longitude } = this.selectedCollection.location;
		const position = {
			lat: latitude,
			lng: longitude,
		};

		this.map.map.setCenter(position);
		this.map.map.setZoom(20);
	}

	/**
	 * Center the map on page load
	 */
	private centerMapOnLoad() {
		const parsedBounds = this.locationFromRoute;
		if (parsedBounds) {
			this.map.map.setCenter(new google.maps.LatLng(parsedBounds.lat, parsedBounds.lng));
			this.map.map.setZoom(parsedBounds.zoom);
		}
	}

	/**
	 * Update the url when the map gets updated after `Services.Analytics.DEBOUNCE_TIME` amount of time
	 */
	private bindMapMoveRouteUpdate() {
		this.disposers.push(
			reaction(
				() => {
					return {
						center: this.map.overlay.bounds && this.map.overlay.bounds.getCenter()?.toJSON(),
						zoom: this.map.overlay.zoom,
					};
				},
				(args) => {
					const boundingString = BoundingString.toString(args.center, args.zoom);
					if (boundingString === '') {
						return;
					}

					Services.Route.setParam('location', boundingString);
				},
				{ delay: Services.Analytics.DEBOUNCE_TIME }
			)
		);
	}

	public getEditCtrlByRevision(revision: string) {
		return this.collectionEditControllers.find((x) => x.revision === revision);
	}

	public removeEditController(revision: string) {
		this.collectionEditControllers = this.collectionEditControllers.filter((x) => x.revision !== revision);
	}

	public collectionHasChanges(revision: string) {
		let collectionEditController = this.collectionEditControllers.find((ctrl) => ctrl.revision === revision);
		if (collectionEditController) {
			return collectionEditController.collectionEditState.isDirty && collectionEditController.isValid;
		}
		return false;
	}

	private bindOnSelectedCollectionUpdate() {
		this.disposers.push(
			reaction(
				() => this.selectedCollection,
				() => {
					if (this.selectedCollection) {
						if (!this.getEditCtrlByRevision(this.selectedCollection.revision)) {
							this.createEditControllerForCollection(this.selectedCollection);
							this.refreshOverlay();
						}

						if (this.selectedCollection.location) {
							const { latitude, longitude } = this.selectedCollection.location;
							const collectionPosition = new google.maps.LatLng(latitude, longitude);

							const updateStreetView = (
								streetView: google.maps.StreetViewPanorama,
								panoData: google.maps.StreetViewPanoramaData
							) => {
								const streetViewPosition = panoData.location.latLng;
								streetView.setPosition(streetViewPosition);
								streetView.setPano(panoData.location.pano);

								const heading = google.maps.geometry.spherical.computeHeading(streetViewPosition, collectionPosition);
								streetView.setPov({
									heading,
									pitch: 15,
								});
							};

							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							Services.Map.isReadyPromise.then(() => {
								const streetViewService = Services.Streetview.streetViewService;
								// eslint-disable-next-line @typescript-eslint/no-floating-promises
								streetViewService.getPanorama(
									{ location: collectionPosition, source: google.maps.StreetViewSource.OUTDOOR },
									(panoData, status) => {
										if (status === google.maps.StreetViewStatus.OK) {
											updateStreetView(Services.Map.map.getStreetView(), panoData);
										}
									}
								);
							});
						}
					}
				}
			)
		);
	}

	private bindOnCollectionModelsUpdate() {
		this.disposers.push(
			reaction(
				() => this.collectionModels,
				() => {
					this.refreshOverlay();
				}
			)
		);
	}

	@computed
	public get contentClassNames() {
		return this.classnames.join(' ');
	}

	@action useTool(toolname: keyof typeof TOPO_TOOL_LOOKUP) {
		const Tool = TOPO_TOOL_LOOKUP[toolname];

		// If this tool is already active then toggle it off
		if (this.activeTool instanceof Tool) {
			this.exitTool();
		} else {
			// Switching to a different tool - exit current and start new
			this.exitTool();
			this.instantiateTool(toolname);
		}
	}

	@action
	private instantiateTool(toolname: keyof typeof TOPO_TOOL_LOOKUP) {
		this.eventContext = Services.Input.getContext();

		if (!TOPO_TOOL_LOOKUP[toolname]) {
			throw new Error(`Could not instantiate Topological Editor tool ${toolname} because it does not exist`);
		}

		this.activeTool = new TOPO_TOOL_LOOKUP[toolname](this);

		this.eventContext.on(this.eventContext.Code.Escape, this.boundExitTool);

		this.map.overlay.setHideTitles(true);
	}

	@action
	public exitTool() {
		if (this.eventContext) {
			this.eventContext.destroy();
		}
		if (this.activeTool) {
			this.activeTool.destroy();

			this.activeTool = null;

			this.map.overlay.setHideTitles(false);
		}
		// Reset create pole mode when exiting any tool
		this.isCreatePoleMode = false;
	}

	public refreshOverlay() {
		if (typeof this.map.overlay !== 'undefined') {
			this.map.overlay.setCollections(this.mapViewCollections);
		}
	}

	public get lineLegendItems(): LegendItem[] {
		const options: { symbolId: string; title: string; symbolColor: string }[] = [];
		this.mapViewCollections.forEach((collection) => {
			collection.savedLineFields().forEach((field) => {
				const option = field.schema.findLineType(field.lineType);
				if (option) {
					options.push({ symbolId: option.symbolId, title: option.title, symbolColor: option.symbolColor });
				}
			});
		});
		return lineStyleLegendItems(options);
	}

	/** Line-draw is app-global now; expose the shared state so existing readers (overlay, popup, guards) keep working. */
	public get lineDrawState(): LineDrawState {
		return CurrentState.LineDraw.state;
	}

	public beginLineDraw(field: CollectionModelFieldLine, lineType: string) {
		CurrentState.LineDraw.begin(field, lineType, {
			standDownOtherTools: () => {
				this.measurementState.reset();
				this.distanceMeasureState.reset();
				this.exitTool();
			},
			onComplete: () => this.refreshOverlay(),
		});
	}

	public cancelLineDraw() {
		CurrentState.LineDraw.cancel();
	}

	public completeLineDraw(): void {
		CurrentState.LineDraw.complete();
	}

	public override destroy() {
		this.exitTool();
		// Clear any line-draw crosshair so it can't leak onto the shared map singleton.
		this.cancelLineDraw();
		this.map.overlay.onRemove();

		super.destroy();
	}

	public batchSave() {
		this.setSaving(true);
		this.exitTool();
		Promise.all(this.collectionEditControllers.map((controller) => controller.batchSaveChanges()))
			.then((result) => {
				this.setSaving(false);
				NotificationService.success({
					key: Translate(T.TOPO_SUCCESS_SAVE_JOB, { amount: result.filter(Boolean).length }),
				});
				this.newPoleModel = [];
				this.collectionEditControllers = [];
				this.refreshOverlay();
			})
			.catch((e) => {
				Services.Log.Logger.error(e, 'TE: Error saving job');
				NotificationService.error({
					key: T.COLLECTION_EDIT_SAVE_ERROR,
				});
				this.setSaving(false);
				this.newPoleModel = [];
				this.refreshOverlay();
			});
	}

	@action setSaving(save: boolean) {
		this.isSaving = save;
	}

	@action.bound setPrintFraming(active: boolean): void {
		this.printFraming = active;
	}

	/**
	 * Loops through all available collections and spans and snaps any straggler spans to its nearest collection within a 3m radius
	 */
	public bulkSnap(): void {
		const spans: PointCollectionLineInfo[] = [];

		// Collect all the spans
		this.mapViewCollections.forEach((collection) => {
			// Filter out non spans & spans that are already linked to another collection
			const vectors = TopologicalPointCollectionCache.getCollectionVectors(collection.revision);
			spans.push(...vectors.filter((x) => x.field.id.includes('spanLength') && !x.field.isCollectionLink()));
		});

		const pendingSnaps: { span: PointCollectionLineInfo; collectionId: string }[] = [];

		spans.forEach((span) => {
			const collectionCandidate: { collectionId: string; distance: number } = { collectionId: null, distance: 3 }; // TODO: PD-966

			// TODO: we can condense this down to an Array.map & Array.sort in the future
			for (const point of this.mapViewCollections) {
				// Skip the collection the span originates from it
				// or if a location is not found
				// of if its a new unsaved pole
				if (point.revision === span.field.collection.revision || !point.location || this.newPoleModel.includes(point)) {
					continue;
				}

				// Get the distance between the span target and the current point
				const distance = IkeGeodesic.getDistance(span.target, {
					latitude: point.location.latitude,
					longitude: point.location.longitude,
					altitude: span.target.altitude,
					/*
                    We use the span target altitude here instead of the point altitude because often the differences in altitude are upwards of 20 meters
                    and if we were to use it, the spans would never snap.

                    Also, fun fact, IkeGeodesic.getDistance never actually uses altitude even though it is a required parameter
                    */
				});

				// If the current point has a closer distance than the active candidate
				if (distance < collectionCandidate.distance) {
					collectionCandidate.collectionId = point.id;
					collectionCandidate.distance = distance;
				}
			}

			// Add the span and the to-be linked collection ID
			if (collectionCandidate.collectionId != null) {
				pendingSnaps.push({
					span,
					collectionId: collectionCandidate.collectionId,
				});
			}
		});

		pendingSnaps.forEach((data) => {
			if (!CollectionModelFieldVector.isVectorField(data.span.field)) {
				return;
			}
			// Create an editable state for the source collection
			if (!this.getEditCtrlByRevision(data.span.field.collection.revision)) {
				this.createEditControllerForCollection(data.span.field.collection);
			}
			// Snap the span to the new collection
			data.span.field.Mutator.collection(data.collectionId);
			// Refresh the map drawing for the source collection
			TopologicalPointCollectionCache.dropCollectionVectors(data.span.field.collection.revision);
		});
	}

	/**
	 * Recalculate distance and bearing from non collection linked spans
	 */
	public async updateSpansDistanceBearing(revision: string) {
		const vectors = TopologicalPointCollectionCache.getCollectionVectors(revision);
		const source = TopologicalPointCollectionCache.getCollectionLocation(revision);

		for (const vector of vectors) {
			if (
				// Its not a vector field
				!CollectionModelFieldVector.isVectorField(vector.field) ||
				// It is collection linked
				vector.field.isCollectionLink() ||
				// It is not a spanType
				!FormModelFieldSubForm.isSpanType(vector.field.parent.schema)
			) {
				continue;
			}

			const updatedVector = IkeGeodesic.getVector(source, vector.target);
			vector.field.Mutator.manual(updatedVector);
		}

		TopologicalPointCollectionCache.dropCollectionVectors(revision);
	}

	private bindReactions() {
		this.reactionToSelectedCaptureId();
		this.reactionToSelectedCollectionRevision();
	}

	private reactionToSelectedCaptureId() {
		this.reaction(
			() => this.selectedCaptureId,
			() => {
				if (Services.Map.isReady) {
					this.map.overlay.setCapture(this.selectedCapture);
				}
			}
		);
	}

	private reactionToSelectedCollectionRevision() {
		this.reaction(
			() => this.selectedCollectionRevision,
			() => {
				if (this.selectedCollectionRevision) {
					when(
						() => !!AppStore.Collection.has(this.selectedCollectionRevision),
						() => {
							if (!Services.Map.isReady) {
								return;
							}
							// The revision can change between the predicate firing and this effect running, so the
							// store/newPole lookup may now miss. Bail rather than deref null. See UFO-582.
							const collection =
								AppStore.Collection.get(this.selectedCollectionRevision) ||
								this.newPoleModel.find((pole) => pole.revision === this.selectedCollectionRevision);
							if (collection == null) {
								return;
							}
							// Capture-less collections (e.g. a line-only pole) have empty captureIds; bail rather than
							// setCapture(null), which crashed in onDraw dereferencing a not-yet-ready projection. See UFO-582.
							const captureIds = this.getCaptureIds();
							if (captureIds.length === 0) {
								return;
							}
							this.map.overlay.setCapture(collection.state.getCapture(captureIds[0]));
						}
					);
				}
			}
		);
	}
}
